import { useEffectEvent } from '@/ui'
import { useAtom } from 'jotai'
import { MouseEvent as ReactMouseEvent, useState } from 'react'
import { transformerReducer } from './reducer'
import { transformerActionStateAtom, transformerPreviewStateAtom, transformerStateAtom } from './state'
import { useTransformerStore } from './transformer-store'

export function useTransformerDrag({ enableSnapping }: { enableSnapping: boolean }) {
	const store = useTransformerStore()
	const [actionState, setActionState] = useAtom(transformerActionStateAtom, { store })
	const [previewState, setPreviewState] = useAtom(transformerPreviewStateAtom, { store })
	const [mainState, setMainState] = useAtom(transformerStateAtom, { store })
	const [lastEventCoordinates, setLastEventCoordinates] = useState<{ x: number; y: number } | null>(null)

	const dragMoveHandler = useEffectEvent((event: MouseEvent) => {
		event.preventDefault()
		event.stopPropagation()
		setLastEventCoordinates({ x: event.clientX, y: event.clientY })
		setPreviewState(() => {
			return transformerReducer(mainState, {
				type: 'handleOffsetChange',
				actionState: actionState,
				dragCoordinates: { x: event.clientX, y: event.clientY },
				snap: enableSnapping ? !event.ctrlKey : false,
			})
		})
	})

	const keyDownHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Control' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleOffsetChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					snap: false,
				})
			})
		}
	})

	const keyUpHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Control' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleOffsetChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					snap: true,
				})
			})
		}
	})

	const dragEndHandler = useEffectEvent(() => {
		setActionState(null)
		if (previewState) {
			setMainState(previewState)
			setPreviewState(null)
		}

		window.removeEventListener('mousemove', dragMoveHandler)
		window.removeEventListener('mouseup', dragEndHandler)
		window.removeEventListener('keydown', keyDownHandler)
		window.removeEventListener('keyup', keyUpHandler)
	})

	const handleDragStart = useEffectEvent((event: ReactMouseEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()

		const newActionState = {
			type: 'offset' as const,
			start: { x: event.clientX, y: event.clientY },
		}

		setActionState(newActionState)

		window.addEventListener('mousemove', dragMoveHandler)
		window.addEventListener('mouseup', dragEndHandler)
		window.addEventListener('keydown', keyDownHandler)
		window.addEventListener('keyup', keyUpHandler)
	})

	return { handleDragStart }
}
